// TopicList.js
import React, { useState, useEffect, useRef } from 'react';
import './TopicList.css';

function TopicList({ topics, activeTopic, setActiveTopic, searchTerm, onDeleteTopic, onRenameTopic }) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const tooltipTimerRef = useRef(null);
  const tooltipRefs = useRef({});

  // Effect to ensure the active topic is visually highlighted
  useEffect(() => {
    document.querySelectorAll('.topic-item').forEach(item => {
      item.classList.remove('selected');
    });
    
    if (activeTopic) {
      const selectedElement = document.querySelector(`.topic-item[data-id="${activeTopic}"]`);
      if (selectedElement) {
        selectedElement.classList.add('selected');
      }
    }
  }, [activeTopic, topics]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
      }
    };
  }, []);

  // Helper function to get topic position for tooltip
  const getTopicPosition = (topicId) => {
    const topicElement = document.querySelector(`.topic-item[data-id="${topicId}"]`);
    if (!topicElement) return { top: 0 };
    
    const rect = topicElement.getBoundingClientRect();
    return {
      top: rect.top,
      height: rect.height
    };
  };
  // Handle mouse enter on topic item - IMPROVED VERSION
  const handleTopicMouseEnter = (topicId) => {
    // Clear any existing hide timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    
    // Using setTimeout to ensure no conflicts with other events
    setTimeout(() => {
      // Show tooltip
      setActiveTooltipId(topicId);
      
      // Ensure tooltip has correct positioning
      setTimeout(() => {
        const tooltip = document.querySelector('.topic-tooltip');
        const topicElement = document.querySelector(`.topic-item[data-id="${topicId}"]`);
        
        if (tooltip && topicElement) {
          const rect = topicElement.getBoundingClientRect();
          tooltip.style.top = `${rect.top}px`;
        }
      }, 10);
    }, 10);
  };
  
  // Handle mouse leave on topic item
  const handleTopicMouseLeave = () => {
    // Set a timer to hide the tooltip after 2.5 seconds
    tooltipTimerRef.current = setTimeout(() => {
      setActiveTooltipId(null);
    }, 2500); // 2.5 seconds
  };
  
  // Handle mouse enter on tooltip
  const handleTooltipMouseEnter = () => {
    // Clear any existing hide timer
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
  };
  
  // Handle mouse leave on tooltip
  const handleTooltipMouseLeave = () => {
    // Set a timer to hide the tooltip after 2.5 seconds
    tooltipTimerRef.current = setTimeout(() => {
      setActiveTooltipId(null);
    }, 2500); // 2.5 seconds
  };

  // Helper function to format timestamps
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  const handleRenameSubmit = (topicId) => {
    if (editTitle.trim()) {
      onRenameTopic(topicId, editTitle.trim());
    }
    setEditingId(null);
  };

  // Sort topics by lastUpdated timestamp (most recent first)
  const sortedTopics = topics ? [...topics].sort((a, b) => {
    const aTimestamp = a.lastUpdated || 0;
    const bTimestamp = b.lastUpdated || 0;
    return bTimestamp - aTimestamp;
  }) : [];

  // Filter topics based on search term
  const filteredTopics = sortedTopics.filter(topic => {
    if (!searchTerm) return true;
    
    const titleMatch = topic.title.toLowerCase().includes(searchTerm.toLowerCase());
    const contentMatch = topic.messages && topic.messages.some(message => 
      message.content && message.content.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return titleMatch || contentMatch;
  });

  // Function to highlight matched text
  const highlightMatchedText = (text, term) => {
    if (!term || !text) return text;
    
    const lowerText = text.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lowerText.indexOf(lowerTerm);
    
    if (index === -1) return text;
    
    return (
      <>
        {text.substring(0, index)}
        <span className="highlight-text">{text.substring(index, index + term.length)}</span>
        {text.substring(index + term.length)}
      </>
    );
  };

  // Count matches in a topic
  const countMatches = (topic, term) => {
    if (!term || !topic || !topic.messages) return 0;
    
    const lowerTerm = term.toLowerCase();
    let count = 0;
    
    if (topic.title.toLowerCase().includes(lowerTerm)) {
      count++;
    }
    
    topic.messages.forEach(message => {
      if (message.content && message.content.toLowerCase().includes(lowerTerm)) {
        const regex = new RegExp(lowerTerm, 'gi');
        const messageMatches = (message.content.match(regex) || []).length;
        count += messageMatches;
      }
    });
    
    return count;
  };
  // Get message count for a topic
  const getMessageCount = (topic) => {
    if (!topic || !topic.messages) return 0;
    return topic.messages.length;
  };

  return (
    <div className="topic-list-container">
      <ul className="topic-list">
        {filteredTopics.map((topic) => {
          const isSearchResult = searchTerm && (
            (topic.title && topic.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (topic.messages && topic.messages.some(message => 
              message.content && message.content.toLowerCase().includes(searchTerm.toLowerCase())
            ))
          );
          
          const matchCount = countMatches(topic, searchTerm);
          const showTooltip = activeTooltipId === topic.id;
          
          return (
            <li key={topic.id} className="topic-item-container">
              {editingId === topic.id ? (
                <div className="topic-edit-form">
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleRenameSubmit(topic.id);
                      } else if (e.key === 'Escape') {
                        setEditingId(null);
                      }
                    }}
                    autoFocus
                  />
                  <button onClick={() => handleRenameSubmit(topic.id)}>✓</button>
                  <button onClick={() => setEditingId(null)}>✕</button>
                </div>
              ) : (
                <div 
                  className={`topic-item ${topic.id === activeTopic ? 'selected' : ''} ${isSearchResult ? 'search-result' : ''}`}
                  onClick={() => setActiveTopic(topic.id)}
                  onMouseEnter={() => handleTopicMouseEnter(topic.id)}
                  onMouseLeave={handleTopicMouseLeave}
                  data-id={topic.id}
                >
                  <div className="topic-title">
                    {isSearchResult && searchTerm
                      ? highlightMatchedText(topic.title, searchTerm)
                      : topic.title}
                    {isSearchResult && searchTerm && matchCount > 0 && (
                      <span className="search-match-indicator">
                        {` (${matchCount} ${matchCount === 1 ? 'match' : 'matches'})`}
                      </span>
                    )}
                  </div>
                  {/* Tooltip with improved positioning */}
                  {showTooltip && (
                    <div 
                      className="topic-tooltip"
                      style={{ 
                        top: `${getTopicPosition(topic.id).top}px` 
                      }}
                      ref={el => tooltipRefs.current[topic.id] = el}
                      onMouseEnter={handleTooltipMouseEnter}
                      onMouseLeave={handleTooltipMouseLeave}
                      onClick={(e) => e.stopPropagation()} // Prevent bubbling
                    >
                      <div className="tooltip-title">{topic.title}</div>
                      <div className="tooltip-info">
                        <div>Messages: {getMessageCount(topic)}</div>
                        <div>Created: {formatDateTime(topic.createdAt || Date.now())}</div>
                        <div>Updated: {formatDateTime(topic.lastUpdated || Date.now())}</div>
                      </div>
                      <div className="tooltip-actions">
                        <button 
                          className="tooltip-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(topic.id);
                            setEditTitle(topic.title);
                            setActiveTooltipId(null);
                          }}
                        >
                          Rename
                        </button>
                        <button 
                          className="tooltip-button delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('Are you sure you want to delete this topic? Any files used only in this conversation will also be removed from the reference system.')) {
                              onDeleteTopic(topic.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
        {searchTerm && filteredTopics.length === 0 && (
          <li className="no-results">No topics match "{searchTerm}"</li>
        )}
      </ul>
    </div>
  );
}

export default TopicList;